import { config } from "config";
import { authController } from "contorller/auth/controller";
import { ElasticPageResult } from "contorller/elastic/types";
import { Fund } from "contorller/fund/redux";
import { GSInfo } from "contorller/simulations/types";
import DownloadWorker from "../../worker_test.js?worker";
import { db, FundHistory, FundIdList, FundListType } from "./db";

const MIN_REFRESH_HOURS_INTERVAL = 1;
const MIN_REFRESH_SECONDS =
  MIN_REFRESH_HOURS_INTERVAL * 60 * 60;

const LOG_LEVEL = false;

const worker: Worker = new DownloadWorker();

interface WorkerResponse extends FundHistory {
  requestKey: string;
  error?: string;
}

interface PendingRequest {
  promise: Promise<FundHistory>;
  resolve: (value: FundHistory) => void;
  reject: (reason?: unknown) => void;
}

const pendingRequests = new Map<string, PendingRequest>();

function normalizeGSInfo(
  fundId: string,
  gsInfo?: GSInfo,
): GSInfo | undefined {
  if (!gsInfo) {
    return undefined;
  }

  return {
    id: fundId,
    retrocessione: gsInfo.retrocessione ?? 1.0,
    floor: gsInfo.floor ?? 0.0,
  };
}

export function makeDBFundId(
  fundId: string,
  gsInfo?: GSInfo,
): string {
  const normalizedGSInfo = normalizeGSInfo(fundId, gsInfo);

  if (!normalizedGSInfo) {
    return fundId;
  }

  return [
    fundId,
    normalizedGSInfo.retrocessione,
    normalizedGSInfo.floor,
  ].join("#|#");
}

export function getFundIdFromDBFundId(
  dbFundId: string,
): string {
  return dbFundId.split("#|#")[0];
}

function isFresh(fundHistory: FundHistory): boolean {
  if (!fundHistory.lastUpdate) {
    return false;
  }

  const ageSeconds =
    Date.now() / 1000 - fundHistory.lastUpdate;

  return ageSeconds <= MIN_REFRESH_SECONDS;
}

/**
 * Unico listener del Worker.
 *
 * Le risposte vengono correlate tramite requestKey.
 */
worker.addEventListener(
  "message",
  (event: MessageEvent<WorkerResponse>) => {
    const response = event.data;

    if (!response?.requestKey) {
      return;
    }

    const pending = pendingRequests.get(response.requestKey);

    if (!pending) {
      return;
    }

    pendingRequests.delete(response.requestKey);

    if (response.error) {
      pending.reject(new Error(response.error));
      return;
    }

    pending.resolve(response);
  },
);

worker.addEventListener("error", (error) => {
  if (LOG_LEVEL) {
    console.error("[FundHistory] Worker error", error);
  }
});

/**
 * Avvia il download tramite Worker.
 *
 * Non aspetta la risposta del Worker.
 */
export async function downloadFund(
  id: string,
  gsInfo?: GSInfo,
  proxy: boolean = false,
): Promise<void> {
  const auther = authController().getAuther();
  const headers = await auther.getHeaderToken();

  const baseUrl = config().URL;

  worker.postMessage({
    headers,
    baseUrl,
    fundId: id,
    gsInfo: normalizeGSInfo(id, gsInfo),
    proxy,
  });
}

/**
 * Effettua una richiesta al Worker.
 *
 * Se esiste già una richiesta per lo stesso fondo/configurazione,
 * viene riutilizzata la Promise esistente.
 */
function requestFund(
  id: string,
  gsInfo?: GSInfo,
  proxy: boolean = true,
): Promise<FundHistory> {
  const requestKey = makeDBFundId(id, gsInfo);

  const existingRequest = pendingRequests.get(requestKey);

  if (existingRequest) {
    return existingRequest.promise;
  }

  let resolveRequest!: (value: FundHistory) => void;
  let rejectRequest!: (reason?: unknown) => void;

  const promise = new Promise<FundHistory>((resolve, reject) => {
    resolveRequest = resolve;
    rejectRequest = reject;
  });

  pendingRequests.set(requestKey, {
    promise,
    resolve: resolveRequest,
    reject: rejectRequest,
  });

  downloadFund(id, gsInfo, proxy).catch((error) => {
    pendingRequests.delete(requestKey);
    rejectRequest(error);
  });

  return promise;
}

export async function getFundData(
  id: string,
  gsInfo?: GSInfo,
  proxy: boolean = true,
): Promise<FundHistory> {
  const cacheKey = makeDBFundId(id, gsInfo);

  if (LOG_LEVEL) {
    console.log(`[FundHistory-${cacheKey}] Get data`);
  }

  const fundHistory = await db.histories.get(cacheKey);

  if (fundHistory && isFresh(fundHistory)) {
    if (LOG_LEVEL) {
      console.log(
        `[FundHistory-${cacheKey}] Found fresh data in storage`,
      );
    }

    return {
      ...fundHistory,
      fundId: getFundIdFromDBFundId(fundHistory.fundId),
    };
  }

  if (LOG_LEVEL) {
    if (fundHistory) {
      console.log(
        `[FundHistory-${cacheKey}] Cached data is expired`,
      );
    } else {
      console.log(
        `[FundHistory-${cacheKey}] Data not found in storage`,
      );
    }

    console.log(
      `[FundHistory-${cacheKey}] Downloading data...`,
    );
  }

  // Il dato è assente o scaduto:
  // in entrambi i casi l'utente deve attendere il nuovo download.
  const result = await requestFund(id, gsInfo, proxy);

  return {
    ...result,
    fundId: getFundIdFromDBFundId(result.fundId),
  };
}

export async function getCompareFundData(
  id: string,
  gsInfo?: GSInfo,
  proxy: boolean = true,
): Promise<{
  fundHistory: FundHistory;
  gsInfo?: GSInfo;
}> {
  const cacheKey = makeDBFundId(id, gsInfo);

  if (LOG_LEVEL) {
    console.log(
      `[FundHistory-${cacheKey}] Get compare data`,
    );
  }

  const fundHistory = await db.histories.get(cacheKey);

  if (fundHistory && isFresh(fundHistory)) {
    if (LOG_LEVEL) {
      console.log(
        `[FundHistory-${cacheKey}] Found fresh data in storage`,
      );
    }

    return {
      fundHistory: {
        ...fundHistory,
        fundId: getFundIdFromDBFundId(fundHistory.fundId),
      },
      gsInfo,
    };
  }

  if (LOG_LEVEL) {
    if (fundHistory) {
      console.log(
        `[FundHistory-${cacheKey}] Cached data is expired`,
      );
    } else {
      console.log(
        `[FundHistory-${cacheKey}] Data not found in storage`,
      );
    }

    console.log(
      `[FundHistory-${cacheKey}] Downloading data...`,
    );
  }

  const result = await requestFund(id, gsInfo, proxy);

  return {
    fundHistory: {
      ...result,
      fundId: getFundIdFromDBFundId(result.fundId),
    },
    gsInfo,
  };
}

// ================================== PARTE PER SCARICARE UNIVERSI IIS & DC

const ISIN_LIST_REFRESH_HOURS = 8;
const ISIN_LIST_REFRESH_SECONDS = ISIN_LIST_REFRESH_HOURS * 60 * 60;

function normalizeId(id: string): string {
  return id.trim();
}

function normalizeIds(ids: string[]): string[] {
  return [...new Set(ids.map(normalizeId).filter(Boolean))];
}

function isIdListFresh(list: FundIdList): boolean {
  const now = Math.floor(Date.now() / 1000);
  return now - list.lastUpdate < ISIN_LIST_REFRESH_SECONDS;
}

async function downloadIdList(
  listType: FundListType
): Promise<string[]> {

  const controllerSignal = new AbortController();
  try {
    const must: Array<any> = [
      {
        exists: {
          field: "bank",
        },
      },
      {
        match: {
          "bank.keyword": {
            query: listType,
          },
        },
      },
    ];
    //TODO: questa dovrebbe iterare tutto, Per il momento siamo abbondantemente sotto i 2000 record quindi teniamola fissa
    const request = {
      from: 0,
      size: 2000,
      query: {
        bool: {
          must: must,
        },
      },
      //TODO: Aggiungere _source in modo che scarichi solo gli id dei fondi
    };
    const response = await fetch(config().ES + "/funds-v2/_search", {
      method: "POST",
      signal: controllerSignal.signal,
      headers: {
        "Content-Type": "application/json",
        Authorization:
          "Bearer " + (await authController().getAuther().getToken()),
      },
      body: JSON.stringify(request),
    });
    if (response.status === 200) {
      let data: ElasticPageResult<Fund> = await response.json();
      return data.hits.hits.map(f => f._source.id)
    } else {
      console.error(`1 - Elastic error downloading list: ${listType}`, response);
      return [];
    }
  } catch (e) {
    console.error(`2 - Elastic error downloading list: ${listType}`, e);
    return [];
  }
}




async function refreshIdList(
  listType: FundListType
): Promise<FundIdList> {
  const ids = normalizeIds(
    await downloadIdList(listType),
  );

  const list: FundIdList = {
    listType,
    ids,
    lastUpdate: Math.floor(Date.now() / 1000),
  };

  await db.fundLists.put(list);

  return list;
}

export async function getIdList(
  listType: FundListType
): Promise<string[]> {
  const cached = await db.fundLists.get(listType);

  if (cached && isIdListFresh(cached)) {
    return cached.ids;
  }

  const refreshed = await refreshIdList(listType);

  return refreshed.ids;
}

export async function isIdInList(
  id: string,
  listType: FundListType
): Promise<boolean> {
  const ids = await getIdList(listType);

  return new Set(ids).has(normalizeId(id));
}

export async function findMissingIds(
  portfolioFundIds: string[],
  listType: FundListType
): Promise<string[]> {
  const ids = await getIdList(listType);
  const idSet = new Set(ids);

  return portfolioFundIds
    .map(normalizeId)
    .filter((isin) => isin && !idSet.has(isin));
}

export async function refreshAllIdLists(): Promise<void> {
  await Promise.all([
    refreshIdList("it_en_dc"),
    refreshIdList("it_en_iis"),
  ]);
}